import { jsx } from 'react/jsx-runtime';
import { createContext, useReducer, useRef, useCallback, useEffect, useMemo, useContext, useId } from 'react';

const modalReducer = (state, action) => {
    switch (action.type) {
        case "REGISTER": {
            const { id } = action.payload;
            if (state.registeredIds.includes(id)) {
                return state;
            }
            return { ...state, registeredIds: [...state.registeredIds, id] };
        }
        case "UNREGISTER": {
            const { id } = action.payload;
            return {
                ...state,
                registeredIds: state.registeredIds.filter((registeredId) => registeredId !== id),
            };
        }
        case "OPEN": {
            if (!state.registeredIds.includes(action.payload.id) ||
                state.stack.includes(action.payload.id)) {
                return state;
            }
            return { ...state, stack: [...state.stack, action.payload.id] };
        }
        case "CLOSE": {
            if (!state.stack.includes(action.payload.id))
                return state;
            return { ...state, stack: state.stack.filter((id) => id !== action.payload.id) };
        }
        case "CLOSE_ALL": {
            if (state.stack.length === 0)
                return state;
            return { ...state, stack: [] };
        }
        default:
            return state;
    }
};
const ModalContext = createContext(null);
const ModalProvider = ({ children }) => {
    const [state, dispatch] = useReducer(modalReducer, {
        registeredIds: [],
        stack: [],
    });
    const modalRefs = useRef(new Map());
    const register = useCallback(({ id }) => dispatch({ type: "REGISTER", payload: { id } }), []);
    const unregister = useCallback(({ id }) => {
        dispatch({ type: "UNREGISTER", payload: { id } });
        modalRefs.current.delete(id);
    }, []);
    const open = useCallback(({ id }) => dispatch({ type: "OPEN", payload: { id } }), []);
    const close = useCallback(({ id }) => dispatch({ type: "CLOSE", payload: { id } }), []);
    const closeAll = useCallback(() => dispatch({ type: "CLOSE_ALL" }), []);
    const isModalOpen = useCallback(({ id }) => state.stack.includes(id), [state.stack]);
    const modalStack = state.stack;
    const focus = useCallback(({ id }) => {
        const modalElement = modalRefs.current.get(id);
        if (modalElement) {
            queueMicrotask(() => modalElement.focus());
        }
    }, []);
    const setModalRef = useCallback(({ id, el }) => {
        if (el) {
            modalRefs.current.set(id, el);
        }
        else {
            modalRefs.current.delete(id);
        }
    }, []);
    useEffect(() => {
        if (modalStack.length > 0) {
            const topModalId = modalStack[modalStack.length - 1];
            const topModalRef = modalRefs.current.get(topModalId);
            if (topModalRef && !topModalRef.contains(document.activeElement)) {
                focus({ id: topModalId });
            }
        }
    }, [modalStack, focus]);
    const contextValue = useMemo(() => ({
        modalStack,
        isModalOpen,
        open,
        close,
        closeAll,
        register,
        unregister,
        focus,
        setModalRef,
    }), [modalStack, isModalOpen, open, close, closeAll, register, unregister, focus, setModalRef]);
    return jsx(ModalContext.Provider, { value: contextValue, children: children });
};
const useModalManager = () => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error("useModalManager must be used within a ModalProvider");
    }
    return context;
};
const useManagedModal = (options = {}) => {
    const context = useModalManager();
    const generatedId = useId();
    const id = options.id ?? generatedId;
    useEffect(() => {
        context.register({ id });
        return () => context.unregister({ id });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, context.register, context.unregister]);
    const onCloseRef = useRef(options.onClose);
    useEffect(() => {
        onCloseRef.current = options.onClose;
    });
    const open = useCallback(() => context.open({ id }), [context, id]);
    const close = useCallback(() => {
        context.close({ id });
        onCloseRef.current?.();
    }, [context, id]);
    const isOpen = context.isModalOpen({ id });
    const refCallback = useCallback((el) => {
        context.setModalRef({ id, el });
    }, [context, id]);
    const modalProps = useMemo(() => ({
        open: isOpen,
        onClose: close,
        ref: refCallback,
    }), [isOpen, close, refCallback]);
    return {
        id,
        props: modalProps,
        open,
        close,
        isOpen,
    };
};

export { ModalProvider, useManagedModal, useModalManager };
//# sourceMappingURL=ModalManager.js.map
